/**
 * The table style library: ways to draw a table, saved under a name and shared
 * across the firm.
 *
 * What travels is a table's look as `look.ts` writes it, and what comes back is
 * read setting by setting through `readTableStyle`: a style saved with another
 * version of the builder still opens.
 */

import { http, type RequestOptions } from '@/api/http'
import type { JsonObject } from '@/domain/json'
import type { TableLook } from '@/sections/table/look'
import { readTableStyle, type TableStyle } from '@/sections/table/tableStyle'

interface StoredTableStyle {
  id: number
  name: string
  owner: string
  style: JsonObject
  is_default: boolean
  usage: number
  updated_at: string
}

interface ListEnvelope {
  tableStyles: StoredTableStyle[]
}

interface OneEnvelope {
  tableStyle: StoredTableStyle
}

interface DeletedEnvelope {
  deleted: boolean
}

/** A look as it is written to the library: the settings it has, and nothing else. */
function written(look: TableLook): JsonObject {
  return { ...look }
}

export async function listTableStyles(options: RequestOptions = {}): Promise<TableStyle[]> {
  const { tableStyles } = await http.get<ListEnvelope>('/tableStyles', options)
  return tableStyles.map(readTableStyle)
}

export async function createTableStyle(
  name: string,
  look: TableLook,
  isDefault = false,
  options: RequestOptions = {},
): Promise<TableStyle> {
  const { tableStyle } = await http.post<OneEnvelope>(
    '/tableStyles',
    { name, style: written(look), is_default: isDefault },
    options,
  )
  return readTableStyle(tableStyle)
}

/** Rename a style, replace what it holds, make it the default, or any of them. */
export async function updateTableStyle(
  id: number,
  changes: { name?: string; look?: TableLook; isDefault?: boolean },
  options: RequestOptions = {},
): Promise<TableStyle> {
  const { tableStyle } = await http.put<OneEnvelope>(
    `/tableStyles/${id}`,
    {
      ...(changes.name !== undefined ? { name: changes.name } : {}),
      ...(changes.look !== undefined ? { style: written(changes.look) } : {}),
      ...(changes.isDefault !== undefined ? { is_default: changes.isDefault } : {}),
    },
    options,
  )
  return readTableStyle(tableStyle)
}

export async function deleteTableStyle(id: number, options: RequestOptions = {}): Promise<boolean> {
  const { deleted } = await http.delete<DeletedEnvelope>(`/tableStyles/${id}`, options)
  return deleted
}
